import type { BeforeSendFn, Properties } from 'posthog-js';

import { REDACTED_URL_SEGMENT, SHIPPING_PAGE } from '../constants/analytics';
import { CLAIM_STEPS } from '../constants/routes';
import { IAnalyticsEvent } from '../types/analytics';

// Case-insensitive: the router matches /O/ and /S/ too, and the first hit fires before the
// path is rewritten to the lowercase template.
// Segments stop at quotes, whitespace and `;` so one match never swallows a second claim path.
const ONE_LINK_PATH = /\/o\/[^/?#"'\s;]+(\/[^/?#"'\s;]+)?/gi;
const SEND_PATH = /\/s\/[^/?#"'\s;]+(\/[^/?#"'\s;]+)?(\/[^/?#"'\s;]+)?/gi;
// A claim path carried inside another value (?next=%2Fo%2Fid) arrives percent-encoded: `/` is %2F
// and a segment ends at an encoded `/`, `?` or `#`. Other escapes inside a segment belong to it.
const ENCODED_SEGMENT = String.raw`(?:[^%/?&#"'\s;]|%(?!2F|3F|23)[0-9A-F]{2})+`;
const ENCODED_ONE_LINK_PATH = new RegExp(`%2Fo%2F${ENCODED_SEGMENT}(%2F${ENCODED_SEGMENT})?`, 'gi');
const ENCODED_SEND_PATH = new RegExp(`%2Fs%2F${ENCODED_SEGMENT}(%2F${ENCODED_SEGMENT})?(%2F${ENCODED_SEGMENT})?`, 'gi');
// Also after `#`: a hash-routed or fragment-carried claim parameter opens the gift just the same.
const CLAIM_QUERY_PARAM = /(^|[?&#])(order_id|key|one_link_id)=[^&#]*/g;

const ENCODED_SLASH = '%2F';

const redactSegment = (segment?: string) => (segment ? `/${REDACTED_URL_SEGMENT}` : '');
const redactEncodedSegment = (segment?: string) => (segment ? `${ENCODED_SLASH}${REDACTED_URL_SEGMENT}` : '');

const isClaimStep = (step: string) => CLAIM_STEPS.includes(step.toLowerCase());

// A trailing segment that is not a known step may be an id copied into :step, so it is redacted too.
const redactStep = (segment?: string) => (segment && isClaimStep(segment.slice(1)) ? segment : redactSegment(segment));

// Step names are plain ASCII, so their encoded form is the name itself.
const redactEncodedStep = (segment?: string) =>
  segment && isClaimStep(segment.slice(ENCODED_SLASH.length)) ? segment : redactEncodedSegment(segment);

const encodedClaimPath = (prefix: string, ...segments: string[]) =>
  `${ENCODED_SLASH}${prefix}${ENCODED_SLASH}${REDACTED_URL_SEGMENT}${segments.join('')}`;

// Claim URLs are capabilities: the one-link id, or the order id and key, open the gift.
// Only claim-shaped substrings are touched, so this is safe on any string.
export const redactClaimUrl = (value: string) =>
  value
    .replace(ONE_LINK_PATH, (_, step) => `/o/${REDACTED_URL_SEGMENT}${redactStep(step)}`)
    .replace(SEND_PATH, (_, key, step) => `/s/${REDACTED_URL_SEGMENT}${redactSegment(key)}${redactStep(step)}`)
    .replace(ENCODED_ONE_LINK_PATH, (_, step) => encodedClaimPath('o', redactEncodedStep(step)))
    .replace(ENCODED_SEND_PATH, (_, key, step) =>
      encodedClaimPath('s', redactEncodedSegment(key), redactEncodedStep(step)),
    )
    .replace(CLAIM_QUERY_PARAM, `$1$2=${REDACTED_URL_SEGMENT}`);

export const getRedactedLocation = () => redactClaimUrl(window.location.href);

// analytics.js copies location and referrer into the tracker at ga('create'), before any redaction,
// and tracker fields only change through ga('set').
export const getRedactedTrackerFields = () => ({
  location: getRedactedLocation(),
  page: redactClaimUrl(`${window.location.pathname}${window.location.search}`),
  referrer: redactClaimUrl(document.referrer),
});

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const proto = Object.getPrototypeOf(value);

  return proto === Object.prototype || proto === null;
};

const redactValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return redactClaimUrl(value);
  }
  if (Array.isArray(value)) {
    return value.map(redactValue);
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, redactValue(nested)]));
  }

  return value;
};

const redactProperties = (properties?: Properties) => properties && (redactValue(properties) as Properties);

// PostHog's default pageview, pageleave and autocapture all carry the browser URL
// ($current_url, $pathname, $session_entry_url, $initial_*, element hrefs).
export const redactPostHogEvent: BeforeSendFn = (result) => {
  if (!result) {
    return result;
  }

  return {
    ...result,
    properties: redactProperties(result.properties) ?? {},
    $set: redactProperties(result.$set),
    $set_once: redactProperties(result.$set_once),
  };
};

export const page = (pageName?: string) => ({
  hitType: 'pageview',
  page: pageName,
});

export const event = ({ eventAction, eventLabel, eventValue }: IAnalyticsEvent) => ({
  hitType: 'event',
  eventCategory: SHIPPING_PAGE,
  ...(eventAction ? { eventAction } : {}),
  ...(eventLabel ? { eventLabel } : {}),
  ...(eventValue !== undefined && eventValue !== null ? { eventValue } : {}),
});

// JSON.stringify(new Error()) is '{}' — message and stack are non-enumerable
const serializeError = (error: any) =>
  JSON.stringify(error instanceof Error ? { name: error.name, message: error.message, stack: error.stack } : error);

export const exception = (error: any) => ({
  hitType: 'exception',
  exDescription: serializeError(error),
  isFatal: true,
});

export const apiException = (error: any) => ({
  hitType: 'exception',
  exDescription: serializeError(error),
  isFatal: false,
});
